import { InfoPopover, RiCompass4Line, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { usePathSet } from '@/hooks/usePathSet';
import { useProjectId } from '@/pages/shared/hooks/useProjectId';
import { FactCapsule } from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/FactCapsule';
import { LegendScale } from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/LegendScale';
import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';
import { useLegendRange } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useLegendRange';
import { useStaticImage } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useStaticImage';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';

import { LandMonitoringCard } from './LandMonitoringCard';

export const HabitatIntactnessCard = () => {
  const { t } = useTranslation();
  const pathSet = usePathSet();
  const projectId = useProjectId();

  const habitatIntactnessMapImage = useStaticImage('habitat_intactness');
  const habitatIntactnessBaselineYear = useFact('r1_conservation_habitat_intactness_baseline_year');

  const habitatIntactnessAverageCurrent = useFact('r1_conservation_threats_i');
  const habitatIntactnessBaseline = useFact('r1_conservation_threats_i_baseline');

  const habitatIntactnessStaticMapExplainer = useFact<string>(
    'r1_conservation_habitat_intactness_static_map_explainer',
  );

  const habitatIntactnessLegendColorsRange = useFact('r1_conservation_habitat_intactness_legend_color_range')?.value as
    | { from?: string; to?: string }
    | undefined;

  const speciesOccupancyProjectAverage = useFact('r1_conservation_species_occupancy_score');
  const floraAndFaunaProjectAverage = useFact('r1_conservation_indicator_species_score');

  const hasIndicatorSpeciesData =
    isNullish(speciesOccupancyProjectAverage?.value) && isNullish(floraAndFaunaProjectAverage?.value);

  const legendRange = useLegendRange('r1_conservation_habitat_intactness_data_range');

  const legendColors = {
    from: habitatIntactnessLegendColorsRange?.from || '#FFF9C5',
    to: habitatIntactnessLegendColorsRange?.to || '#8E3104',
  };

  const threatsTimeSeries = useFact('r1_conservation_threats_i_ts');
  const threatsTimeSeriesBaselineYear = threatsTimeSeries?.value
    ? Object.keys(threatsTimeSeries.value).sort((a, b) => Number(a) - Number(b))[0]
    : null;

  return (
    <LandMonitoringCard
      data-testid='habitat-intactness'
      className={hasIndicatorSpeciesData ? 'max-w-1/2 flex-none' : undefined}
      title={t('shared.ncaDetail.details.landMonitoring.habitatIntactness.title')}
      heading={{
        heading: (
          <ChartHeading
            heading={t('shared.ncaDetail.details.landMonitoring.habitatIntactness.heading')}
            popover={
              <InfoPopover
                title={t(
                  'shared.ncaDetail.details.landMonitoring.habitatIntactness.explainers.remoteSensedThreats.title',
                )}
                body={t(
                  'shared.ncaDetail.details.landMonitoring.habitatIntactness.explainers.remoteSensedThreats.body',
                )}
                popoverTriggerProps={{
                  className: 'text-text-secondary',
                }}
              />
            }
          />
        ),
      }}
      image={{
        url: habitatIntactnessMapImage,
        alt: t('shared.ncaDetail.details.landMonitoring.habitatIntactness.mapImageAlt'),
      }}
      imageTootilp={habitatIntactnessStaticMapExplainer?.value}
      detailsPagePath={buildPath(pathSet.habitatIntactness, { pathParams: { projectId } })}
      stats={
        <Stack spacing={6}>
          <Stack spacing={10} className='py-3'>
            <FactCapsule
              label={t('shared.ncaDetail.details.ecologicalIntegrity.labels.projectAverageValue')}
              capsuleIconProps={{ icon: <RiCompass4Line />, variant: 'default' }}
              fact={habitatIntactnessAverageCurrent}
            />
            <FactCapsule
              label={t('shared.ncaDetail.ecologicalIntegrity.breakdown.habitatIntactness.baselinePrevFiveYears', {
                year: habitatIntactnessBaselineYear?.value ?? threatsTimeSeriesBaselineYear,
              })}
              capsuleIconProps={{ icon: <RiCompass4Line />, variant: 'default' }}
              fact={habitatIntactnessBaseline}
            />
          </Stack>
          <Stack spacing={2} className='p-2'>
            <div
              className='h-3 w-full max-w-[222px] rounded-sm'
              style={{
                backgroundImage: `linear-gradient(to right, ${legendColors.from}, ${legendColors.to})`,
              }}
            />

            <Stack direction='row' className='justify-between'>
              <LegendScale customLegendRange={legendRange} />
            </Stack>
          </Stack>
        </Stack>
      }
    />
  );
};
